import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import { useStore } from "../../context/StoreContext";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetModifierGroupsQuery,
  useCreateModifierGroupMutation,
  useUpdateModifierGroupMutation,
  useDeleteModifierGroupMutation,
} from "../../store/api";

const getGroupOrderKey = (group, index = 0) => {
  const groupId = group?.modifierGroupId ?? group?.id;
  if (groupId !== undefined && groupId !== null && groupId !== "") {
    return `id:${String(groupId)}`;
  }
  return `tmp:${group?.name || group?.modifierGroupName || "row"}:${index}`;
};

const Modifiers = () => {
  const navigate = useNavigate();
  const { selectedStoreId } = useStore();
  const {
    data: groups,
    isLoading,
    isError,
    error,
  } = useGetModifierGroupsQuery(selectedStoreId, { skip: !selectedStoreId });
  const [createGroup, createGroupState] = useCreateModifierGroupMutation();
  const [updateGroup, updateGroupState] = useUpdateModifierGroupMutation();
  const [deleteGroup, deleteGroupState] = useDeleteModifierGroupMutation();

  const [groupForm, setGroupForm] = useState({
    name: "",
    minSelections: 0,
    maxSelections: 1,
    isRequired: false,
    isActive: true,
  });
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    groupId: null,
    groupName: "",
  });

  const getGroupId = (group) => group?.modifierGroupId ?? group?.id;
  const groupOrderRef = useRef(new Map());
  const groupOrderSeqRef = useRef(0);

  const groupsList = useMemo(() => {
    const source = Array.isArray(groups) ? groups : [];

    source.forEach((group, index) => {
      const orderKey = getGroupOrderKey(group, index);
      if (!groupOrderRef.current.has(orderKey)) {
        groupOrderRef.current.set(orderKey, groupOrderSeqRef.current++);
      }
    });

    return [...source]
      .map((group, index) => ({ group, index }))
      .sort((a, b) => {
        const aKey = getGroupOrderKey(a.group, a.index);
        const bKey = getGroupOrderKey(b.group, b.index);
        const aOrder = groupOrderRef.current.get(aKey) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = groupOrderRef.current.get(bKey) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      })
      .map(({ group }) => group);
  }, [groups]);

  const handleGroupChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGroupForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      alert("Please select a store first");
      return;
    }
    try {
      const payload = {
        ...groupForm,
        minSelections: Number(groupForm.minSelections),
        maxSelections: Number(groupForm.maxSelections),
        minSelect: Number(groupForm.minSelections),
        maxSelect: Number(groupForm.maxSelections),
        selectionType:
          Number(groupForm.maxSelections) === 1 ? "SINGLE" : "MULTIPLE",
      };
      if (editingGroupId) {
        await updateGroup({
          storeId: selectedStoreId,
          groupId: editingGroupId,
          payload,
        }).unwrap();
        setEditingGroupId(null);
      } else {
        await createGroup({ storeId: selectedStoreId, payload }).unwrap();
      }
      setGroupForm({
        name: "",
        minSelections: 0,
        maxSelections: 1,
        isRequired: false,
        isActive: true,
      });
      setShowGroupForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroupId(getGroupId(group));
    setGroupForm({
      name: group.name || group.modifierGroupName || "",
      minSelections: group.minSelections ?? group.minSelect ?? 0,
      maxSelections: group.maxSelections ?? group.maxSelect ?? 1,
      isRequired: group.isRequired || false,
      isActive: group.isActive !== undefined ? group.isActive : true,
    });
    setShowGroupForm(true);
  };

  const handleDeleteGroup = (group) => {
    setDeleteModal({
      isOpen: true,
      groupId: getGroupId(group),
      groupName: group.name || group.modifierGroupName || "this group",
    });
  };

  const confirmDeleteGroup = async () => {
    try {
      await deleteGroup({
        storeId: selectedStoreId,
        groupId: deleteModal.groupId,
      }).unwrap();
    } catch (err) {
      // error handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        groupId: null,
        groupName: "",
      });
    }
  };

  const handleCancelGroup = () => {
    setEditingGroupId(null);
    setGroupForm({
      name: "",
      minSelections: 0,
      maxSelections: 1,
      isRequired: false,
      isActive: true,
    });
    setShowGroupForm(false);
  };

  const handleToggleActive = async (group) => {
    const groupId = getGroupId(group);
    const minSelections = group.minSelections ?? group.minSelect ?? 0;
    const maxSelections = group.maxSelections ?? group.maxSelect ?? 1;
    const isActive =
      group.isActive !== undefined && group.isActive !== null
        ? group.isActive
        : true;
    try {
      await updateGroup({
        storeId: selectedStoreId,
        groupId,
        payload: {
          name: group.name || group.modifierGroupName || "",
          minSelections: Number(minSelections),
          maxSelections: Number(maxSelections),
          minSelect: Number(minSelections),
          maxSelect: Number(maxSelections),
          isRequired: group.isRequired || false,
          isActive: !isActive,
          selectionType: Number(maxSelections) === 1 ? "SINGLE" : "MULTIPLE",
        },
      }).unwrap();
    } catch (err) {
      // error handled by UI
    }
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "selectionType", label: "Selection Type" },
    { key: "minSelect", label: "Min Select" },
    { key: "maxSelect", label: "Max Select" },
    { key: "sort", label: "Sort" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const {
    page,
    totalCount,
    pageSize,
    paginatedItems: paginatedGroups,
    setPage,
    setPageSize,
  } = useClientPagination(groupsList, { pageSize: 10 });

  const rows = paginatedGroups.map((group, index) => {
    const groupId = getGroupId(group);
    const rowId =
      groupId !== undefined && groupId !== null && groupId !== ""
        ? String(groupId)
        : `modifier-group-${group.name || group.modifierGroupName || "row"}-${index}`;
    const minSelect = group.minSelect ?? group.minSelections ?? 0;
    const maxSelect = group.maxSelect ?? group.maxSelections ?? 1;
    const selectionType =
      group.selectionType || (Number(maxSelect) === 1 ? "SINGLE" : "MULTIPLE");
    const isActive =
      group.isActive !== undefined && group.isActive !== null
        ? group.isActive
        : true;

    return {
      id: rowId,
      name: group.name || group.modifierGroupName || "-",
      selectionType,
      minSelect,
      maxSelect,
      sort: group.sort ?? group.sortOrder ?? 0,
      status: (
        <div className="flex items-center gap-2">
          <ActionCheckbox
            checked={isActive}
            label={isActive ? "Active" : "Inactive"}
            onChange={() => handleToggleActive(group)}
          />
          <span className="text-sm text-slate-700">
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <ActionButton
            label="Options"
            icon="view"
            onClick={() =>
              navigate(`/menu/modifiers/${encodeURIComponent(groupId)}/options`)
            }
          />
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEditGroup(group)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDeleteGroup(group)}
          />
        </div>
      ),
    };
  });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Add Ons"
          subtitle="Manage add-on groups and options."
        />
        <Card>
          <Alert tone="warning">
            Please select a store to manage add-ons.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Add Ons"
        subtitle="Create add-on groups with options for menu items."
        actions={
          showGroupForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancelGroup}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowGroupForm(true)}>
              Add On Group
            </Button>
          )
        }
      />
      {showGroupForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingGroupId ? "Edit Add On Group" : "Add On Group"}
          </h2>
          <form className="space-y-4" onSubmit={handleGroupSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Group Name"
                name="name"
                placeholder="Size"
                value={groupForm.name}
                onChange={handleGroupChange}
                required
              />
              <Checkbox
                label="Required"
                name="isRequired"
                checked={groupForm.isRequired}
                onChange={handleGroupChange}
                helper="Customer must select an option"
              />
              <Checkbox
                label="Active"
                name="isActive"
                checked={groupForm.isActive}
                onChange={handleGroupChange}
                helper="Inactive groups stay hidden from item configuration"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Minimum Selections"
                name="minSelections"
                type="number"
                min="0"
                placeholder="0"
                value={groupForm.minSelections}
                onChange={handleGroupChange}
                helper="Minimum options customer must select"
              />
              <Input
                label="Maximum Selections"
                name="maxSelections"
                type="number"
                min="1"
                placeholder="1"
                value={groupForm.maxSelections}
                onChange={handleGroupChange}
                helper="Maximum options customer can select"
              />
            </div>

            {(createGroupState.isSuccess || updateGroupState.isSuccess) && (
              <Alert tone="success">
                Add On group {editingGroupId ? "updated" : "created"}{" "}
                successfully.
              </Alert>
            )}
            {(createGroupState.isError || updateGroupState.isError) && (
              <Alert tone="error">
                {createGroupState.error?.data?.detail ||
                  updateGroupState.error?.data?.detail ||
                  "Failed to save add-on group."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={
                  createGroupState.isLoading || updateGroupState.isLoading
                }
              >
                {editingGroupId ? "Update" : "Add"} Group
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancelGroup}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showGroupForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            All Add On Groups
          </div>
          <div className="px-6 py-4">
            {isLoading && (
                <p className="text-sm text-slate-500">
                Loading add-on groups...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load add-on groups."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No add-on groups yet."
                />
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  isLoading={isLoading}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            groupId: null,
            groupName: "",
          })
        }
        title="Delete Add On Group"
        confirmText="Delete"
        onConfirm={confirmDeleteGroup}
        isLoading={deleteGroupState.isLoading}
      >
        <p>
          Are you sure you want to delete <strong>{deleteModal.groupName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default Modifiers;
